// ViewClientModal.jsx

import Modal from "../ui/Modal";
import { useRef } from "react";
import useClientModalDrop from "./useClientModalDrop";
import uploadIcon from "../../images/upload-icon-1.svg";
import "./AddClientModal.css";

function toDateOrNull(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateValue(v) {
  const d = toDateOrNull(v);
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(d);
}

function showOrDash(v) {
  const s = (v ?? "").toString().trim();
  return s ? s : "—";
}

function countryCodeToName(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return "";

  try {
    // "en" gives English country names
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(c) || c;
  } catch {
    return c;
  }
}

// -------------------------------------------------------
// -----------------   EXPORT FUNCTION   -----------------
// -------------------------------------------------------

export default function ViewClientModal({ open, onClose, client, onEdit }) {
  const cardRef = useRef(null);
  const dragging = useClientModalDrop({ enabled: !!(open && client && onEdit), cardRef,
    onFiles: files => onEdit(client, files) });
  if (!client) {
    return (
      <Modal
        open={open}
        title="Client details"
        onClose={onClose}
        className="viewClientModal"
        footer={
          <button
            type="button"
            className="btn btn-sm btn-modal-cancel"
            onClick={onClose}
          >
            Close
          </button>
        }
      >
        <div className="vc-muted">No client selected.</div>
      </Modal>
    );
  }

  const isIndividual = client.clientType === "individual";
  const isEntity = client.clientType === "entity";

  const isProspect = client.status === "prospect";
  const isClient = client.status === "client";
  const isFormerClient = client.status === "former client";

  const typeLabel = isIndividual
    ? "Individual"
    : isEntity
    ? "Entity"
    : showOrDash(client.clientType);
  const statusLabel = showOrDash(client.status);

  const statusIconClass =
    client.status === "client"
      ? "bi bi-shield-check typeIcon typeIconClient"
      : client.status === "prospect"
      ? "bi bi-binoculars typeIcon typeIconProspect"
      : client.status === "former client"
      ? "bi bi-archive typeIcon typeIconFormerClient"
      : "bi bi-question-circle typeIcon";

  return (
    <Modal
      open={open}
      title="Client details"
      onClose={onClose}
      className="viewClientModal"
      cardRef={cardRef}
      overlay={dragging ? <div className="addClientDocumentDropOverlay" aria-hidden="true">
        <div className="addClientDocumentDropContent">
          <span className="addClientDocumentDropIcon" style={{ "--upload-icon": `url("${uploadIcon}")` }} />
          <span>Drop an ID, proof of address, trade licence or CV</span>
        </div>
      </div> : null}
      footer={
        <>
          {onEdit && (
            <button
              type="button"
              className="btn btn-sm btn-modal-cancel btn-w1"
              style={{ backgroundColor: "#D4CAC4" }}
              onClick={() => onEdit(client)}
            >
              <i className="bi bi-pencil-square me-2" aria-hidden="true" />
              Edit
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-modal-save btn-w3"
            onClick={onClose}
          >
            Close
          </button>
        </>
      }
    >
      {/* HEADER */}
      <div className="vc-head">
        <div className="vc-titleRow">
          <div className="vc-name client-name-viewModal">
            {showOrDash(client.clientName)}
          </div>

          <div className="vc-badges">
            {/* Type badge */}
            <span className="vc-badge">
              {isIndividual ? (
                <i
                  className="bi bi-person me-1 typeIcon typeIconIndividual"
                  aria-hidden="true"
                />
              ) : isEntity ? (
                <i
                  className="bi bi-building me-1 typeIcon typeIconEntity"
                  aria-hidden="true"
                />
              ) : null}
              {typeLabel}
            </span>

            {/* Status badge */}
            <span className="vc-badge">
              <i className={`me-1 ${statusIconClass}`} aria-hidden="true" />
              {statusLabel}
            </span>

            {/* KYC badge (if you store it on the client doc) */}
            {typeof client.kycCompleted === "boolean" && (
              <span className="vc-badge">
                {client.kycCompleted ? "KYC: Complete" : "KYC: Uncomplete"}
              </span>
            )}
          </div>
        </div>

        {/* Optional small subtitle line */}
        {/* <div className="vc-sub vc-muted">
          ID: <span className="vc-mono">{showOrDash(client.id)}</span>
        </div> */}
      </div>

      {/* CONTENT GRID */}
      <div className="vc-grid">
        {/* ADDRESS */}
        <div className="vc-section">
          <div className="vc-sectionTitle">Address</div>

          <div className="vc-kv">
            <div className="vc-kvRow">
              <div className="vc-k">Street</div>
              <div className="vc-v">{showOrDash(client.address)}</div>
            </div>

            <div className="vc-kvRow">
              <div className="vc-k">City</div>
              <div className="vc-v">{showOrDash(client.city)}</div>
            </div>

            <div className="vc-kvRow">
              <div className="vc-k">Country</div>
              <div className="vc-v">
                {showOrDash(countryCodeToName(client.country))}
              </div>
              {/* <div className="vc-v">{showOrDash(client.country)}</div> */}
            </div>
          </div>
        </div>

        {/* CONTACT */}
        <div className="vc-section">
          <div className="vc-sectionTitle">Contact</div>

          <div className="vc-kv">
            {/* Only show “Contact name” for entities (same rule as Edit) */}
            {isEntity && (
              <div className="vc-kvRow">
                <div className="vc-k">Name</div>
                <div className="vc-v">{showOrDash(client.contactName)}</div>
              </div>
            )}

            {/* Email + phone for both individual/entity (same rule as Edit) */}
            {(isIndividual || isEntity) && (
              <>
                <div className="vc-kvRow">
                  <div className="vc-k">Email</div>
                  <div className="vc-v">
                    {client.contactEmail ? (
                      <a
                        className="vc-link"
                        href={`mailto:${client.contactEmail}`}
                      >
                        {client.contactEmail}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>

                <div className="vc-kvRow">
                  <div className="vc-k">Phone</div>
                  <div className="vc-v">
                    {client.contactPhone ? (
                      <a
                        className="vc-link"
                        href={`tel:${client.contactPhone}`}
                      >
                        {client.contactPhone}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* DATES */}
        {!isProspect && (
          <div className="vc-section">
            <div className="vc-sectionTitle">Dates</div>

            <div className="vc-kv">
              {(isClient || isFormerClient) && (
                <div className="vc-kvRow">
                  <div className="vc-k">Join date</div>
                  <div className="vc-v">{formatDateValue(client.joinDate)}</div>
                </div>
              )}

              {isFormerClient && (
                <div className="vc-kvRow">
                  <div className="vc-k">Leave date</div>
                  <div className="vc-v">
                    {formatDateValue(client.leaveDate)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
